import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import ConfirmationPopup from "./ConfirmationPopup";
import { requestOtp, verifyOtp } from "../data";
import { colors } from "../../../utils/styles";

const RESEND_SECONDS = 30;

function OtpStage({ token, userEmail, onVerified }) {
  const [phase, setPhase] = useState("idle"); // idle | sending | sent | verifying | error
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const countingDown = resendIn > 0;

  useEffect(() => {
    if (!countingDown) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [countingDown]);

  const handleSend = async () => {
    if (!token) return;
    setPhase("sending");
    setMessage("");
    try {
      await requestOtp(token);
      setPhase("sent");
      setMessage(`Code sent to ${userEmail || "your email"}.`);
      setResendIn(RESEND_SECONDS);
    } catch (err) {
      if (err.retryAfterSeconds) {
        setPhase("sent");
        setMessage(`Please wait before requesting another code.`);
        setResendIn(err.retryAfterSeconds);
      } else {
        setPhase("error");
        setMessage("Failed to send code. Try again in a moment.");
      }
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setMessage("Enter the 6-digit code from your email.");
      return;
    }
    setPhase("verifying");
    setMessage("");
    try {
      const ok = await verifyOtp(token, code);
      if (ok) {
        onVerified();
      } else {
        setPhase("sent");
        setMessage("Incorrect or expired code. Try again.");
      }
    } catch {
      setPhase("error");
      setMessage("Verification failed. Please try again.");
    }
  };

  return (
    <>
      <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.navy }}>Verify your identity</h1>
      <p style={{ margin: "12px 0 16px", fontSize: 13, color: colors.textMuted }}>
        {userEmail ? (
          <>
            We will send a 6-digit code to <strong>{userEmail}</strong>. Enter the code to start the
            Preliminary Notification form.
          </>
        ) : (
          "Loading your account..."
        )}
      </p>

      {phase === "idle" && (
        <Button onClick={handleSend} disabled={!token || !userEmail} variant="contained">
          Send code
        </Button>
      )}

      {(phase === "sending" || phase === "verifying") && (
        <p style={{ margin: 0, fontSize: 13, color: colors.textMuted }}>
          {phase === "sending" ? "Sending code..." : "Verifying..."}
        </p>
      )}

      {(phase === "sent" || phase === "error") && (
        <>
          <TextField
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="6-digit code"
            fullWidth
            slotProps={{
              htmlInput: {
                inputMode: "numeric",
                pattern: "\\d{6}",
                maxLength: 6,
                style: { fontSize: 18, letterSpacing: 4, color: colors.navy }
              }
            }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button onClick={handleVerify} variant="contained">
              Verify
            </Button>
            <Button onClick={handleSend} disabled={resendIn > 0} variant="outlined">
              {resendIn > 0 ? `Resend (${resendIn}s)` : "Resend code"}
            </Button>
          </Stack>
        </>
      )}

      {message && (
        <p
          style={{
            margin: "12px 0 0",
            fontSize: 12,
            color: phase === "error" ? colors.error : colors.textMuted
          }}
        >
          {message}
        </p>
      )}
    </>
  );
}

export default function PreFormPopup({ token, userEmail, otpDisabled, onVerified }) {
  // Two-stage gate: stage "otp" runs the OTP flow, then "acknowledge" shows the
  // mandatory read-and-confirm checkbox. When otpDisabled is true we skip straight
  // to the acknowledgment stage but still require it.
  const [stage, setStage] = useState(otpDisabled ? "acknowledge" : "otp");

  return (
    <Dialog
      open
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, p: 1 } } }}
    >
      <DialogContent>
        {stage === "otp" ? (
          <OtpStage token={token} userEmail={userEmail} onVerified={() => setStage("acknowledge")} />
        ) : (
          <ConfirmationPopup
            title="Important notice"
            checkboxLabel="I have read and understood the statement above."
            confirmLabel="Continue to form"
            onConfirm={onVerified}
          >
            Approved users with Read/Write access in the Direct Charges portal will be able to input data for
            their approved jurisdictions (subagencies). If your agency has multiple users with Read/Write
            access, it is recommended that your agency designates which user will input the data for the
            online Preliminary Notification of Direct Charges to avoid duplicate submissions. By checking this
            box, you confirm that you have read this statement.
          </ConfirmationPopup>
        )}
      </DialogContent>
    </Dialog>
  );
}
